import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Copy,
  Edit,
  KeyRound,
  MessageCircle,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import MainService from '../../services/MainService';
import SmsKeywordTextField, { SMS_KEYWORDS } from '../Messages/SmsKeywordTextField';

const PAGE_SIZE = 10;

const whatsappTypes = [
  'Order',
  'Delivery Note',
  'Delivered',
  'Cancelled',
  'Returned',
  'Payment',
  'Return',
  'Creditor',
  'Debitor',
  'Client',
  'App Delivery',
  'App Pickup',
];

const emptyForm = {
  Id: 0,
  Type: '',
  Message: '',
  IsEnable: false,
  IsClient: false,
  IsCustomer: false,
  IsSendFile: false,
  TemplateId: '',
  AuthHeader: '',
  APIURL: '',
  ParamsArray: '',
  Language: '',
};

const parseRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getTotal = (response) => Number(response?.Total ?? response?.total ?? 0);

const StatusPill = ({ enabled, label }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
    {enabled ? label : `No ${label}`}
  </span>
);

const WhatsappConfiguration = () => {
  const [rows, setRows] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [keywordModalOpen, setKeywordModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isEditing = Number(formData.Id) > 0;

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.WhatsAppMessagesInGrid({
        Skip: (currentPage - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });
      setRows(parseRows(response));
      setTotalCount(getTotal(response));
    } catch (err) {
      console.error('Load WhatsApp messages error:', err);
      setRows([]);
      setTotalCount(0);
      setError('Failed to load WhatsApp messages');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess(`Copied "${text}" to clipboard`);
  };

  const editRow = (row) => {
    setFormData({
      ...emptyForm,
      ...row,
      IsEnable: row.IsEnable === true,
      IsClient: row.IsClient === true,
      IsCustomer: row.IsCustomer === true,
      IsSendFile: row.IsSendFile === true,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveData = async () => {
    if (!formData.Type) {
      setError('Select Type');
      return;
    }
    if (!formData.Message?.trim()) {
      setError('Enter Message');
      return;
    }
    if (!formData.TemplateId?.trim()) {
      setError('Enter Template Id');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const response = await MainService.SaveWhatsAppMessage(formData);
      if (response === true || response === 'true' || response === 'Success') {
        setSuccess('WhatsApp message saved successfully');
        resetForm();
        await loadRows();
      } else {
        setError('Failed to save WhatsApp message');
      }
    } catch (err) {
      console.error('Save WhatsApp message error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const deleteData = async () => {
    if (!deleteTarget?.Id) return;

    try {
      setLoading(true);
      const response = await MainService.DeleteWhatsAppMessage(deleteTarget.Id);
      if (response === true || response === 'true' || response === 'Success') {
        setSuccess('Deleted');
        setDeleteTarget(null);
        await loadRows();
      } else {
        setError('Failed to delete WhatsApp message');
      }
    } catch (err) {
      console.error('Delete WhatsApp message error:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center">
            <MessageCircle className="mr-2 h-6 w-6 text-[#46955A]" />
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Whatsapp Configuration</h1>
              <p className="mt-1 text-sm text-gray-500">Manage WhatsApp templates, API settings, and message keywords</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outlined"
              startIcon={<KeyRound className="h-4 w-4" />}
              onClick={() => setKeywordModalOpen(true)}
              sx={{
                borderColor: '#46955A',
                color: '#46955A',
                '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' },
              }}
            >
              Key References
            </Button>
            <Button
              variant="outlined"
              startIcon={<Plus className="h-4 w-4" />}
              onClick={resetForm}
              disabled={saving}
              sx={{
                borderColor: '#6B7280',
                color: '#6B7280',
                '&:hover': { borderColor: '#4B5563', backgroundColor: '#F9FAFB' },
              }}
            >
              New
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
          <div className="xl:col-span-2">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
              <h2 className="mb-4 text-lg font-semibold text-gray-900">{isEditing ? 'Update WhatsApp Message' : 'Add WhatsApp Message'}</h2>
              <div className="grid grid-cols-1 gap-4">
                <TextField
                  select
                  label="Type"
                  value={formData.Type}
                  onChange={(e) => updateField('Type', e.target.value)}
                  size="small"
                >
                  {whatsappTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Template Id"
                  value={formData.TemplateId}
                  onChange={(e) => updateField('TemplateId', e.target.value)}
                  size="small"
                />

                <div className="grid grid-cols-2 gap-2">
                  <FormControlLabel control={<Checkbox checked={formData.IsEnable} onChange={(e) => updateField('IsEnable', e.target.checked)} sx={{ color: '#46955A', '&.Mui-checked': { color: '#46955A' } }} />} label="Enable" />
                  <FormControlLabel control={<Checkbox checked={formData.IsSendFile} onChange={(e) => updateField('IsSendFile', e.target.checked)} sx={{ color: '#46955A', '&.Mui-checked': { color: '#46955A' } }} />} label="Send File" />
                  <FormControlLabel control={<Checkbox checked={formData.IsClient} onChange={(e) => updateField('IsClient', e.target.checked)} sx={{ color: '#46955A', '&.Mui-checked': { color: '#46955A' } }} />} label="Client" />
                  <FormControlLabel control={<Checkbox checked={formData.IsCustomer} onChange={(e) => updateField('IsCustomer', e.target.checked)} sx={{ color: '#46955A', '&.Mui-checked': { color: '#46955A' } }} />} label="Customer" />
                </div>

                <SmsKeywordTextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={formData.Message}
                  onValueChange={(value) => updateField('Message', value)}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      '&:hover fieldset': { borderColor: '#46955A' },
                      '&.Mui-focused fieldset': { borderColor: '#46955A' },
                    },
                  }}
                />

                <TextField
                  label="Auth Header"
                  value={formData.AuthHeader}
                  onChange={(e) => updateField('AuthHeader', e.target.value)}
                  multiline
                  rows={2}
                  size="small"
                />
                <TextField
                  label="API URL"
                  value={formData.APIURL}
                  onChange={(e) => updateField('APIURL', e.target.value)}
                  multiline
                  rows={2}
                  size="small"
                />
                <TextField
                  label="Params"
                  placeholder="[@tag1,@tag2,@tag3,@tag4...]"
                  value={formData.ParamsArray}
                  onChange={(e) => updateField('ParamsArray', e.target.value)}
                  multiline
                  rows={2}
                  size="small"
                />
                <TextField
                  label="Language"
                  value={formData.Language}
                  onChange={(e) => updateField('Language', e.target.value)}
                  size="small"
                />

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outlined" startIcon={<RefreshCw className="h-4 w-4" />} onClick={resetForm} disabled={saving}>
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Save className="h-4 w-4" />}
                    onClick={saveData}
                    disabled={saving}
                    sx={{ backgroundColor: '#46955A', '&:hover': { backgroundColor: '#3d7d4f' } }}
                  >
                    {isEditing ? 'Update' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="xl:col-span-3">
            <div className="rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-200 p-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">WhatsApp Messages</h2>
                  <p className="text-sm text-gray-500">10 records per page</p>
                </div>
                <Button variant="outlined" onClick={loadRows} disabled={loading} startIcon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}>
                  Refresh
                </Button>
              </div>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress size={32} sx={{ color: '#46955A' }} />
                </Box>
              ) : rows.length === 0 ? (
                <Box sx={{ color: '#6B7280', textAlign: 'center', py: 8 }}>No WhatsApp messages available</Box>
              ) : (
                <div className="max-h-[680px] divide-y divide-gray-100 overflow-y-auto">
                  {rows.map((row) => (
                    <div key={row.Id} className="p-4 transition-colors hover:bg-gray-50">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className="rounded bg-[#f0f9f4] px-3 py-1 text-sm font-semibold text-[#46955A] ring-1 ring-[#46955A]/20">{row.Type || 'N/A'}</span>
                            <StatusPill enabled={row.IsEnable} label="Enabled" />
                            <StatusPill enabled={row.IsClient} label="Client" />
                            <StatusPill enabled={row.IsCustomer} label="Customer" />
                            <StatusPill enabled={row.IsSendFile} label="File" />
                          </div>
                          <p className="line-clamp-3 whitespace-pre-wrap text-sm text-gray-800">{row.Message || '-'}</p>
                          <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-500 md:grid-cols-2">
                            <div><span className="font-semibold text-gray-700">Template:</span> {row.TemplateId || '-'}</div>
                            <div><span className="font-semibold text-gray-700">Language:</span> {row.Language || '-'}</div>
                            <div className="md:col-span-2"><span className="font-semibold text-gray-700">API:</span> {row.APIURL || '-'}</div>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <button type="button" onClick={() => editRow(row)} className="rounded-lg p-2 text-yellow-600 hover:bg-yellow-50" title="Edit">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => setDeleteTarget(row)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-gray-600">
                  Showing {rows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage <= 1 || loading}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage >= totalPages || loading}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={keywordModalOpen} onClose={() => setKeywordModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#111827', fontWeight: 700 }}>Keyword References</DialogTitle>
        <DialogContent dividers>
          <p className="mb-4 text-sm text-gray-500">Click any keyword to copy it. You can also type @ in the message box to insert keywords.</p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {SMS_KEYWORDS.map((keyword, index) => (
              <div key={index} className="flex items-center justify-between rounded-lg border border-gray-200 p-3 transition-colors hover:border-[#46955A] hover:bg-gray-50">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-700">{keyword.name}</div>
                  <div className="mt-1 font-mono text-xs text-gray-500">{keyword.keyword}</div>
                </div>
                <Tooltip title="Copy to clipboard">
                  <IconButton size="small" onClick={() => copyToClipboard(keyword.keyword)} sx={{ color: '#46955A', '&:hover': { backgroundColor: '#f0f9f4' } }}>
                    <Copy className="h-4 w-4" />
                  </IconButton>
                </Tooltip>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeywordModalOpen(false)} sx={{ color: '#46955A' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {deleteTarget && (
        <Dialog open onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: '#111827', fontWeight: 700 }}>Confirm Delete</DialogTitle>
          <DialogContent dividers>
            <p className="text-sm text-gray-600">Delete WhatsApp message configuration for <strong>{deleteTarget.Type || 'N/A'}</strong>?</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)} disabled={loading}>Cancel</Button>
            <Button onClick={deleteData} disabled={loading} variant="contained" color="error">Confirm</Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border px-4 py-3 shadow-lg ${isError ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
      {isError ? <AlertTriangle className="h-5 w-5 flex-shrink-0" /> : <CheckCircle className="h-5 w-5 flex-shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} className={isError ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default WhatsappConfiguration;
